import { useState } from "react";
import PageLayout from "@/components/PageLayout";
import { api } from "@/lib/api";
import { Heart } from "lucide-react";

const PRESETS = [
    { id: "tier_25", amount: 25, blurb: "A warm meal for a family" },
    { id: "tier_50", amount: 50, blurb: "School supplies starter" },
    { id: "tier_100", amount: 100, blurb: "An event seat for a caregiver" },
    { id: "tier_250", amount: 250, blurb: "Emergency support pack" },
];

export default function Donate() {
    const [selected, setSelected] = useState("tier_50");
    const [customAmount, setCustomAmount] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const isCustom = selected === "custom";

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            const body = {
                origin_url: window.location.origin,
                donor_name: name,
                donor_email: email,
            };
            if (isCustom) {
                const amt = parseFloat(customAmount);
                if (!amt || amt < 1) {
                    setError("Please enter a valid donation amount.");
                    setLoading(false);
                    return;
                }
                body.custom_amount = amt;
            } else {
                body.package_id = selected;
            }
            const { data } = await api.post("/donate/checkout", body);
            window.location.href = data.url;
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not start checkout. Please try again.");
            setLoading(false);
        }
    };

    return (
        <PageLayout>
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-12">
                <div data-testid="donate-intro">
                    <div className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase text-white px-3 py-1 rounded-full bg-brand-purple">
                        <Heart size={14} /> Give Today
                    </div>
                    <h1 className="mt-5 font-serif text-5xl sm:text-6xl tracking-tight leading-[1.05]">Support a family today</h1>
                    <div className="font-accent text-3xl text-brand-purple mt-2 -rotate-2">your gift, their tomorrow</div>
                    <p className="mt-6 text-stone-600 leading-relaxed text-lg">
                        100% of your donation goes directly to programs that lift up grandparents and kinship caregivers. Secure checkout via Stripe.
                    </p>
                    <ul className="mt-8 space-y-3 text-stone-700">
                        <li>• Emergency support for caregivers</li>
                        <li>• Community events &amp; family gatherings</li>
                        <li>• Educational resources &amp; mentoring</li>
                    </ul>
                </div>

                <form onSubmit={submit} className="bg-brand-cream rounded-3xl p-8 md:p-10 border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="donate-form">
                    <div className="text-sm uppercase tracking-[0.25em] text-stone-500">Choose an amount</div>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                        {PRESETS.map((p) => (
                            <button
                                type="button"
                                key={p.id}
                                onClick={() => setSelected(p.id)}
                                data-testid={`donate-preset-${p.id}`}
                                className={`text-left rounded-2xl px-5 py-4 border-2 transition ${
                                    selected === p.id ? "border-brand-purple bg-purple-50" : "border-stone-200 hover:border-stone-300"
                                }`}
                            >
                                <div className="font-serif text-3xl">${p.amount}</div>
                                <div className="text-xs text-stone-500 mt-1">{p.blurb}</div>
                            </button>
                        ))}
                        <button
                            type="button"
                            onClick={() => setSelected("custom")}
                            data-testid="donate-preset-custom"
                            className={`text-left rounded-2xl px-5 py-4 border-2 col-span-2 transition ${
                                isCustom ? "border-brand-purple bg-purple-50" : "border-stone-200 hover:border-stone-300"
                            }`}
                        >
                            <div className="font-serif text-2xl">Custom amount</div>
                            <div className="text-xs text-stone-500 mt-1">Pick the amount that feels right</div>
                        </button>
                    </div>

                    {isCustom && (
                        <div className="mt-5">
                            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">Amount (USD)</label>
                            <input
                                type="number"
                                min="1"
                                step="0.01"
                                required
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                                placeholder="e.g., 75"
                                data-testid="donate-custom-amount"
                                className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-purple"
                            />
                        </div>
                    )}

                    <div className="mt-6 grid sm:grid-cols-2 gap-3">
                        <input
                            placeholder="Your name (optional)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            data-testid="donate-input-name"
                            className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-purple"
                        />
                        <input
                            type="email"
                            placeholder="Email (optional)"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="donate-input-email"
                            className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-purple"
                        />
                    </div>

                    {error && <div className="mt-4 text-sm text-brand-red" data-testid="donate-error">{error}</div>}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="donate-submit"
                        className="mt-8 w-full px-7 py-4 rounded-full bg-brand-purple text-white font-semibold tracking-wide hover:bg-brand-purple-light transition disabled:opacity-50"
                    >
                        {loading ? "Redirecting..." : "Continue to secure checkout →"}
                    </button>
                    <p className="text-xs text-stone-500 mt-3 text-center">Payments processed securely via Stripe</p>
                </form>
            </section>
        </PageLayout>
    );
}
